import { useAuth } from "@clerk/clerk-react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Input,
  Link,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { OrganizationUser } from "schemas/dashboard";

import Alert from "components/Alert";
import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

interface Niche {
  id: number;
  name: string;
  isArchived: boolean;
  status: string;
  locationName?: string;
}

interface OrgNicheCache {
  niches: Niche[];
  loading: boolean;
}

interface SelectedOrg {
  organizationId: number;
  name: string;
  email: string;
}

interface PastUpload {
  filename: string;
  url: string;
  isDryRun: boolean;
  date: string | null;
}

const UploadBrands = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const [orgUsers, setOrgUsers] = useState<OrganizationUser[]>([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const [selectedOrgs, setSelectedOrgs] = useState<SelectedOrg[]>([]);
  const [orgNiches, setOrgNiches] = useState<Record<number, OrgNicheCache>>({});
  const [selectedNicheIds, setSelectedNicheIds] = useState<Set<number>>(
    new Set(),
  );
  const [prioritizedTags, setPrioritizedTags] = useState<string[]>([]);
  const [pastUploads, setPastUploads] = useState<PastUpload[]>([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const fetchPastUploads = async () => {
    setUploadsLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/saved-brand-collections/uploads",
        "GET",
      );
      setPastUploads(res.uploads || []);
    } catch {
      // non-critical
    }
    setUploadsLoading(false);
  };

  useEffect(() => {
    if (tab === 1) {
      fetchPastUploads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const getReportType = (filename: string): string => {
    if (filename.endsWith(".bento-brands-data.csv")) {
      return "Brand Results";
    }
    return "Saved Brands Report";
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateStr;
    }
  };

  const sortedUploads = useMemo(() => {
    return [...pastUploads].sort((a, b) => {
      const dateA = a.date || "";
      const dateB = b.date || "";
      return sortDirection === "asc"
        ? dateA.localeCompare(dateB)
        : dateB.localeCompare(dateA);
    });
  }, [pastUploads, sortDirection]);

  const fetchOrgUsers = async (query?: string) => {
    setOrgSearchLoading(true);
    try {
      const { organizationUsers } = await fetcherAuth(
        getToken,
        query
          ? `/api/admin/organization-users?query=${query}`
          : "/api/admin/organization-users",
        "GET",
      );
      setOrgUsers(organizationUsers);
    } catch {
      setAlert("Failed to fetch organizations", "warning");
    }
    setOrgSearchLoading(false);
  };

  useEffect(() => {
    fetchOrgUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (inputValue) {
        fetchOrgUsers(inputValue);
      } else {
        fetchOrgUsers();
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const fetchNichesForOrg = useCallback(
    async (orgId: number) => {
      setOrgNiches((prev) => ({
        ...prev,
        [orgId]: { niches: prev[orgId]?.niches || [], loading: true },
      }));
      try {
        const res = await fetcherAuth(
          getToken,
          `/api/admin/virtual-assistant-users/${orgId}/niches`,
          "GET",
        );
        setOrgNiches((prev) => ({
          ...prev,
          [orgId]: { niches: res.niches || [], loading: false },
        }));
      } catch {
        setOrgNiches((prev) => ({
          ...prev,
          [orgId]: { niches: [], loading: false },
        }));
      }
    },
    [getToken],
  );

  const handleOrgSelect = (_: unknown, newValue: OrganizationUser | null) => {
    if (!newValue) return;
    const alreadySelected = selectedOrgs.some(
      (o) => o.organizationId === newValue.organizationId,
    );
    if (alreadySelected) return;

    const newOrg: SelectedOrg = {
      organizationId: newValue.organizationId,
      name: newValue.name,
      email: newValue.email,
    };
    setSelectedOrgs((prev) => [...prev, newOrg]);

    if (!orgNiches[newOrg.organizationId]) {
      fetchNichesForOrg(newOrg.organizationId);
    }
  };

  const handleRemoveOrg = (orgId: number) => {
    setSelectedOrgs((prev) => prev.filter((o) => o.organizationId !== orgId));
    setSelectedNicheIds((prev) => {
      const next = new Set(prev);
      const nichesForOrg = orgNiches[orgId]?.niches || [];
      for (const niche of nichesForOrg) {
        next.delete(niche.id);
      }
      return next;
    });
  };

  const toggleNiche = (nicheId: number) => {
    setSelectedNicheIds((prev) => {
      const next = new Set(prev);
      if (next.has(nicheId)) next.delete(nicheId);
      else next.add(nicheId);
      return next;
    });
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] || null);
  };

  const getSelectedNicheNames = (): string[] => {
    const names: string[] = [];
    for (const org of selectedOrgs) {
      const niches = orgNiches[org.organizationId]?.niches || [];
      for (const niche of niches) {
        if (selectedNicheIds.has(niche.id)) {
          const nicheName =
            niche.name ||
            `Niche #${niche.id}` +
              (niche.locationName ? ` (${niche.locationName})` : "");
          names.push(`${nicheName} (${org.name})`);
        }
      }
    }
    return names;
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      if (!file) {
        throw new Error("You must select a CSV file to upload");
      }
      const fileExt = file.name.split(".").pop()?.toLowerCase();
      if (fileExt !== "csv") {
        throw new Error("You must select a CSV file");
      }
      if (selectedNicheIds.size === 0) {
        throw new Error(
          "You must select atleast one organization and its associated niche",
        );
      }
      setConfirmOpen(true);
    } catch (error) {
      setAlert(error?.message || "An error occurred", "warning");
    }
  };

  const handleConfirmUpload = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const options: Record<string, unknown> = {
        dry_run: false,
        update_if_exists: false,
        organization_niche_ids: Array.from(selectedNicheIds),
        prioritized_tags: prioritizedTags.length ? prioritizedTags : undefined,
      };

      const data = new FormData();
      data.append("file", file!);
      data.append("options", JSON.stringify(options));

      await fetcherAuth(
        getToken,
        "/api/admin/saved-brand-collections/create-from-csv",
        "POST",
        {},
        data,
        false,
        true,
      );
      setAlert("Upload started successfully", "success");
    } catch (error) {
      setAlert(error?.message || "An error occurred", "warning");
    } finally {
      setLoading(false);
    }
  };

  const confirmSummary = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
      <Box>
        <Typography variant="body2" fontWeight={600}>
          File
        </Typography>
        <Typography variant="body2">{file?.name}</Typography>
      </Box>
      <Box>
        <Typography variant="body2" fontWeight={600}>
          Organizations
        </Typography>
        {selectedOrgs.map((org) => (
          <Typography key={org.organizationId} variant="body2">
            {org.name} - {org.email}
          </Typography>
        ))}
      </Box>
      <Box>
        <Typography variant="body2" fontWeight={600}>
          Niches ({selectedNicheIds.size})
        </Typography>
        {getSelectedNicheNames().map((name) => (
          <Typography key={name} variant="body2">
            {name}
          </Typography>
        ))}
      </Box>
      {prioritizedTags.length > 0 && (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            Tags
          </Typography>
          <Typography variant="body2">{prioritizedTags.join(", ")}</Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <StyledPage
      content={
        <Grid sx={{ mt: 2, pb: 10 }}>
          <Typography variant="h3" gutterBottom>
            <strong>Upload Brands</strong>
          </Typography>

          <Tabs
            value={tab}
            onChange={(_, newTab) => setTab(newTab)}
            sx={{ mb: 3 }}
          >
            <Tab label="Upload" />
            <Tab label="Past Uploads" />
          </Tabs>

          {tab === 0 && (
            <>
              <form onSubmit={handleFormSubmit}>
                {/* Step 1: CSV File */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    1. Select a CSV file of brands to upload
                  </Typography>
                  <Input type="file" onChange={handleFileChange} />
                </Box>

                {/* Step 2: Add Organizations */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    2. Add organizations
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Search by name, email, or organization ID.
                  </Typography>
                  <Autocomplete
                    disablePortal
                    loading={orgSearchLoading}
                    options={orgUsers}
                    getOptionLabel={(option) =>
                      `${option.name} - ${option.email} | ${option.organizationId}`
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search by name, email, or organization ID"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {orgSearchLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                    onChange={handleOrgSelect}
                    onInputChange={(_event, newInputValue) => {
                      setInputValue(newInputValue);
                    }}
                    value={null}
                    blurOnSelect
                  />
                  {selectedOrgs.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 1,
                      }}
                    >
                      {selectedOrgs.map((org) => (
                        <Chip
                          key={org.organizationId}
                          label={`${org.name} - ${org.email}`}
                          onDelete={() => handleRemoveOrg(org.organizationId)}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Step 3: Select Niches */}
                {selectedOrgs.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      gutterBottom
                    >
                      3. Select niches
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Choose which niches should receive the uploaded brands.
                    </Typography>
                    {selectedOrgs.map((org) => {
                      const cache = orgNiches[org.organizationId];
                      const niches = cache?.niches || [];
                      return (
                        <Box key={org.organizationId} sx={{ mb: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            {org.name} - {org.email}
                          </Typography>
                          {cache?.loading ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Loading niches...
                            </Typography>
                          ) : niches.length === 0 ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No niches found
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {niches.map((niche) => {
                                const isSelected = selectedNicheIds.has(
                                  niche.id,
                                );
                                const statusLabel = niche.isArchived
                                  ? "archived"
                                  : niche.status === "active"
                                    ? "active"
                                    : niche.status;
                                const nicheName =
                                  niche.name ||
                                  `Niche #${niche.id}` +
                                    (niche.locationName
                                      ? ` (${niche.locationName})`
                                      : "");
                                return (
                                  <Chip
                                    key={niche.id}
                                    label={`${nicheName} (${statusLabel})`}
                                    disabled={
                                      !(
                                        niche.status === "active" &&
                                        !niche.isArchived
                                      )
                                    }
                                    onClick={() => {
                                      if (
                                        niche.status === "active" &&
                                        !niche.isArchived
                                      ) {
                                        toggleNiche(niche.id);
                                      }
                                    }}
                                    color={
                                      isSelected
                                        ? "primary"
                                        : niche.status === "active" &&
                                            !niche.isArchived
                                          ? "success"
                                          : "default"
                                    }
                                    variant={isSelected ? "filled" : "outlined"}
                                    size="small"
                                    sx={
                                      niche.isArchived
                                        ? {
                                            opacity: 0.5,
                                            textDecoration: "line-through",
                                          }
                                        : undefined
                                    }
                                  />
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Brand Tags (optional)
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    These tags will be added to every brand in the CSV.
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={prioritizedTags}
                    onChange={(_, newValue) => setPrioritizedTags(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tags"
                        placeholder="Type a tag and press Enter"
                        size="small"
                      />
                    )}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </form>

              <ConfirmDialog
                open={confirmOpen}
                header="Confirm Upload"
                subheader={confirmSummary}
                handleClose={() => setConfirmOpen(false)}
                handleConfirm={handleConfirmUpload}
                loading={loading}
                confirmText="Upload"
                cancelText="Cancel"
              />
            </>
          )}

          {tab === 1 && (
            <Box>
              {uploadsLoading ? (
                <CircularProgress size={20} />
              ) : pastUploads.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No past uploads found.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active
                            direction={sortDirection}
                            onClick={() =>
                              setSortDirection((prev) =>
                                prev === "asc" ? "desc" : "asc",
                              )
                            }
                          >
                            Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>File</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedUploads.map((upload) => (
                        <TableRow key={upload.filename} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Typography variant="body2" fontWeight={600}>
                              {formatDate(upload.date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getReportType(upload.filename)}
                              size="small"
                              color={
                                upload.filename.endsWith(
                                  ".bento-brands-data.csv",
                                )
                                  ? "info"
                                  : "success"
                              }
                              variant="outlined"
                            />
                            {upload.isDryRun && (
                              <Chip
                                label="Dry Run"
                                size="small"
                                color="warning"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 400, overflow: "hidden" }}>
                            <Link
                              href={upload.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {upload.filename}
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
        </Grid>
      }
    />
  );
};

export default UploadBrands;
